'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FloatingWhatsApp from '@/components/FloatingWhatsApp';
import ThemeProvider from '@/components/ThemeProvider';

// --- INTERFACES UNTUK TYPESCRIPT ---
interface GalleryImage {
  id: string | number;
  src: string;
  alt: string;
  selected: boolean;
}

const materials = [
  {
    id: 'mat-1',
    title: 'Aluminium Composite Panel',
    desc: 'CNC router ACP dirancang khusus untuk memproses panel komposit seperti ACP dan HPL dengan efisiensi tinggi.',
    image: '/assets/cncrouter/Acp.png',
    alt: 'Close-up of intricate laser-cut metal panel',
  },
  {
    id: 'mat-2',
    title: 'PVC Board',
    image: '/assets/cncrouter/pvc.jpeg',
    alt: 'Material PVC Board untuk CNC Router - papan pengganti kayu tahan air',
    desc: 'Material pengganti kayu yang digunakan untuk keperluan tahan air dan anti rayap.'
  },
  {
    id: 'mat-3',
    title: 'Akrilik',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_190c7525e-1767804766178.png",
    alt: 'Material Akrilik untuk CNC Router - plastik bening premium untuk display',
    desc: 'Akrilik bening premium untuk huruf timbul, display, dan produk iklan.'
  },
  {
    id: 'mat-5',
    title: 'Aluminium',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d89721a0-1778124777110.png",
    alt: 'Material aluminium untuk CNC Router - plat aluminium ringan dan kuat',
    desc: 'Plat aluminium ringan dan kuat untuk kebutuhan industri dan konstruksi.'
  }
];

const highlights = [
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8"><path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>,
    title: 'Free Ongkir',
    subtitle: 'Jabodetabek'
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8"><path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
    title: 'Free Jasa',
    subtitle: 'Desain Custom'
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8"><path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>,
    title: 'Free Konsultasi',
    subtitle: 'Teknis'
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    title: '24 Jam',
    subtitle: 'Produksi'
  }
];

const features = [
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7"><path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    title: 'Workshop Strategis',
    desc: 'Lokasi di Jl. Bogor Rawa. Indah No.45, RW.01, Setiamulya, Kec. Tarumajaya, Kabupaten Bekasi, Jawa Barat 17213'
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7"><path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>,
    title: 'Presisi Tinggi',
    desc: 'Menggunakan teknologi sensor dan kontrol numerik komputer (CNC) yang menjamin tingkat akurasi hingga detail terkecil.'
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7"><path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138z" /></svg>,
    title: 'Hasil Rapi & Minim Finishing',
    desc: 'Menggunakan teknologi sensor dan kontrol numerik komputer (CNC) yang menjamin tingkat akurasi hingga detail terkecil.'
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    title: 'Efisiensi Material',
    desc: 'Proses penataan pola (nesting) yang optimal memastikan penggunaan bahan yang lebih hemat dan efisien bagi pelanggan.'
  }
];

const galleryImages: GalleryImage[] = [
  { id: 1, src: "https://img.rocket.new/generatedImages/rocket_gen_img_1b82d00d7-1772269260896.png", alt: 'CNC Router MDF', selected: true },
  { id: 2, src: "https://img.rocket.new/generatedImages/rocket_gen_img_148c9dae1-1778124775679.png", alt: 'CNC Router PVC', selected: true },
  { id: 3, src: "https://img.rocket.new/generatedImages/rocket_gen_img_190c7525e-1767804766178.png", alt: 'CNC Router Akrilik', selected: true },
  { id: 4, src: "https://img.rocket.new/generatedImages/rocket_gen_img_18239a5f6-1773417607487.png", alt: 'Workshop', selected: true },
  { id: 5, src: "https://img.rocket.new/generatedImages/rocket_gen_img_100bf9733-1772269260372.png", alt: 'Panel Dinding', selected: true },
  { id: 6, src: "https://img.rocket.new/generatedImages/rocket_gen_img_1366210aa-1778124777116.png", alt: 'Backdrop', selected: true },
  { id: 7, src: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1f9d256-1778124776812.png", alt: 'Signage', selected: true },
  { id: 8, src: "https://img.rocket.new/generatedImages/rocket_gen_img_1c022a156-1778124775690.png", alt: 'Partisi', selected: true },
  { id: 9, src: "https://img.rocket.new/generatedImages/rocket_gen_img_15643aef4-1772102088187.png", alt: 'Cutting Aluminium', selected: true },
  { id: 29, src: '/assets/galeri/1.png', alt: '', selected: true },
  { id: 30, src: '/assets/galeri/2.png', alt: '', selected: false },
  { id: 31, src: '/assets/galeri/3.png', alt: '', selected: true },
  { id: 32, src: '/assets/galeri/4.png', alt: ' ', selected: true },
  { id: 33, src: '/assets/galeri/5.png', alt: ' ', selected: true },
  { id: 35, src: '/assets/galeri/7.jpg', alt: ' ', selected: true },
  { id: 36, src: '/assets/galeri/8.jpg', alt: ' ', selected: true },
  { id: 37, src: '/assets/galeri/9.jpg', alt: ' ', selected: true },
  { id: 38, src: '/assets/galeri/10.jpg', alt: ' ', selected: true },
  { id: 39, src: '/assets/galeri/11.jpg', alt: ' ', selected: true },
  { id: 40, src: '/assets/galeri/12.jpg', alt: ' ', selected: true },
  { id: 41, src: '/assets/galeri/13.jpg', alt: ' ', selected: true },
  { id: 42, src: '/assets/galeri/14.jpg', alt: ' ', selected: true },
  { id: 43, src: '/assets/galeri/15.jpg', alt: ' ', selected: true },
  { id: 44, src: '/assets/galeri/16.jpg', alt: ' ', selected: true },
  { id: 45, src: '/assets/galeri/17.jpg', alt: ' ', selected: true },
  { id: 46, src: '/assets/galeri/18.jpg', alt: ' ', selected: true },
  { id: 47, src: '/assets/galeri/19.jpg', alt: ' ', selected: true },
  { id: 48, src: '/assets/galeri/20.jpg', alt: ' ', selected: true },
  { id: 49, src: '/assets/galeri/21.jpg', alt: ' ', selected: true },
  { id: 50, src: '/assets/galeri/22.jpg', alt: ' ', selected: true },
  { id: 51, src: '/assets/galeri/23.jpg', alt: ' ', selected: true },
  { id: 52, src: '/assets/galeri/24.jpg', alt: ' ', selected: true },
  { id: 53, src: '/assets/galeri/25.png', alt: ' ', selected: true },
  { id: 54, src: '/assets/galeri/26.jpeg', alt: ' ', selected: true },
  { id: 55, src: '/assets/galeri/27.jpeg', alt: ' ', selected: true },
  { id: 56, src: '/assets/galeri/28.jpeg', alt: ' ', selected: true },
  { id: 57, src: '/assets/galeri/29.jpeg', alt: ' ', selected: true },
  { id: 58, src: '/assets/galeri/30.jpeg', alt: ' ', selected: true },
  { id: 59, src: '/assets/galeri/31.jpeg', alt: ' ', selected: true },
  { id: 60, src: '/assets/galeri/32.jpeg', alt: ' ', selected: true },
  { id: 61, src: '/assets/galeri/33.jpeg', alt: ' ', selected: true },
  { id: 62, src: '/assets/galeri/34.jpeg', alt: ' ', selected: true },
  { id: 63, src: '/assets/galeri/35.jpeg', alt: ' ', selected: true },
  { id: 64, src: '/assets/galeri/36.jpeg', alt: '', selected: true }
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } }
};

export default function CNCRouterPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all'); 
  const [selectedImg, setSelectedImg] = useState<GalleryImage | null>(null);

  const visibleCount = 3;
  const maxSlide = materials?.length - visibleCount;

  const handlePrev = () => setCurrentSlide((prev) => Math.max(0, prev - 1));
  const handleNext = () => setCurrentSlide((prev) => Math.min(maxSlide, prev + 1));

  // Logika filter (Slice setengah data)
  const filteredImages = activeFilter === 'selected' 
    ? galleryImages.slice(0, Math.ceil(galleryImages.length / 2)) 
    : galleryImages;

  // Gabungkan material dan galeri untuk navigasi pop-up
  const allPopupImages: GalleryImage[] = [
    ...materials.map((m, idx) => ({ id: `mat-${idx}`, src: m.image, alt: m.alt, selected: true })),
    ...galleryImages
  ];

  // Navigasi Modal
  const handleModalNext = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!selectedImg) return;
    const currentIndex = allPopupImages.findIndex(img => img.src === selectedImg.src);
    const nextIndex = (currentIndex + 1) % allPopupImages.length;
    setSelectedImg(allPopupImages[nextIndex]);
  }, [selectedImg, allPopupImages]);

  const handleModalPrev = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!selectedImg) return;
    const currentIndex = allPopupImages.findIndex(img => img.src === selectedImg.src);
    const prevIndex = (currentIndex - 1 + allPopupImages.length) % allPopupImages.length;
    setSelectedImg(allPopupImages[prevIndex]);
  }, [selectedImg, allPopupImages]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedImg) return;
      if (e.key === 'ArrowRight') handleModalNext();
      if (e.key === 'ArrowLeft') handleModalPrev();
      if (e.key === 'Escape') setSelectedImg(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImg, handleModalNext, handleModalPrev]);

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
        <Header />
        <main>
          {/* Hero Banner */}
          <section className="bg-[#1E1B4B] dark:bg-[#0B2E26] py-14 px-4 text-center text-white">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-extrabold mb-3">
              Pusat Jasa CNC Router 
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-white/70 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
              Jasa CNC Router profesional dengan presisi tinggi untuk berbagai material — PVC, Akrilik, dan lebih banyak lagi.
            </motion.p>
          </section>

          {/* Material Slider Section */}
          <section className="py-16 px-4 bg-background">
            <div className="max-w-4xl mx-auto">
              <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-2xl md:text-3xl font-extrabold text-center mb-10">
                Material yang Kami Kerjakan
              </motion.h2>

              <div className="relative overflow-hidden">
                <motion.div
                  className="flex gap-4 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
                  style={{ transform: `translateX(calc(-${currentSlide * (100 / visibleCount)}% - ${currentSlide * 16 / visibleCount}px))` }}>
                  {materials?.map((mat, i) => (
                    <div 
                      key={i} 
                      onClick={() => setSelectedImg({ id: `mat-${i}`, src: mat.image, alt: mat.alt, selected: true })}
                      className="flex-shrink-0 bg-card rounded-2xl border border-border shadow-soft overflow-hidden cursor-pointer group" 
                      style={{ width: `calc(${100 / visibleCount}% - ${(visibleCount - 1) * 16 / visibleCount}px)` }}
                    >
                      <div className="aspect-[4/3] overflow-hidden relative">
                        <img src={mat?.image} alt={mat?.alt} className="w-full h-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                        <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                           <span className="bg-white/20 backdrop-blur-md text-white text-[10px] px-3 py-1 rounded-full border border-white/30">LIHAT DETAIL</span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-base mb-1">{mat?.title}</h3>
                        <p className="text-muted-foreground text-xs leading-relaxed">{mat?.desc}</p>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>

              <div className="flex justify-center items-center gap-3 mt-6">
                <button onClick={handlePrev} disabled={currentSlide === 0} className="w-8 h-8 rounded-full border flex items-center justify-center disabled:opacity-30">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                {Array.from({ length: maxSlide + 1 })?.map((_, i) => (
                  <button key={i} onClick={() => setCurrentSlide(i)} className={`w-2.5 h-2.5 rounded-full ${i === currentSlide ? 'bg-accent w-6' : 'bg-border'}`} />
                ))}
                <button onClick={handleNext} disabled={currentSlide === maxSlide} className="w-8 h-8 rounded-full border flex items-center justify-center disabled:opacity-30">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M9 18l6-6-6-6" /></svg>
                </button>
              </div>
            </div>
          </section>

          {/* Highlight Section */}
          <section className="py-12 px-4 bg-background border-t border-border">
            <div className="max-w-4xl mx-auto text-center">
              <motion.p variants={fadeUp} initial="hidden" whileInView="visible" className="font-semibold text-base mb-8 text-foreground">Dapatkan Keuntungan Lebih Bersama Kami:</motion.p>
              <motion.div variants={stagger} initial="hidden" whileInView="visible" className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {highlights?.map((item, i) => (
                  <motion.div key={i} variants={fadeUp} className="group bg-card rounded-2xl border p-5 flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-xl bg-accent/10 dark:bg-accent/20 flex items-center justify-center text-accent transition-transform duration-300 group-hover:scale-110">
                      {item?.icon}
                    </div>
                    <div><p className="font-bold text-sm leading-tight">{item?.title}</p><p className="text-accent text-xs font-semibold">{item?.subtitle}</p></div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* Kenapa Memilih Kami Section */}
          <section className="py-16 px-4 bg-[#1E1B4B] dark:bg-[#0B2E26] text-white">
            <div className="max-w-4xl mx-auto">
              <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" className="text-2xl md:text-3xl font-extrabold text-center mb-10">
                Kenapa Memilih Kami?
              </motion.h2>
              <motion.div variants={stagger} initial="hidden" whileInView="visible" className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {features?.map((feat, i) => (
                  <motion.div key={i} variants={fadeUp} className="bg-white/5 rounded-2xl border border-white/10 p-5 flex flex-col gap-3 hover:bg-white/10 transition-colors duration-300">
                    <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center text-accent">{feat?.icon}</div>
                    <h3 className="font-bold text-sm leading-tight">{feat?.title}</h3>
                    <p className="text-white/60 text-xs leading-relaxed">{feat?.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* Gallery Section */}
          <section className="py-16 px-4 bg-background">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-extrabold text-foreground mb-4">Galeri & Workshop</h2>
                <div className="flex justify-center gap-3 mt-4">
                  <button 
                    onClick={() => setActiveFilter('selected')}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${activeFilter === 'selected' ? 'bg-[#1E1B4B] dark:bg-accent text-white border-transparent shadow-lg' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                    Selected Images
                  </button>
                  <button 
                    onClick={() => setActiveFilter('all')}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${activeFilter === 'all' ? 'bg-[#1E1B4B] dark:bg-accent text-white border-transparent shadow-lg' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                    All Images
                  </button>
                </div>
              </div>

              <motion.div layout variants={stagger} initial="hidden" whileInView="visible" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                <AnimatePresence mode="popLayout">
                  {filteredImages?.map((img) => (
                    <motion.div
                      layout
                      key={img.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      onClick={() => setSelectedImg(img)}
                      className="aspect-[4/3] rounded-lg overflow-hidden bg-muted cursor-pointer relative group border border-border">
                      <img src={img?.src} alt={img?.alt} className="w-full h-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold">LIHAT DETAIL</div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            </div>
          </section>
        </main>
        
        <Footer />
        <FloatingWhatsApp />

        {/* --- MODAL LIGHTBOX DENGAN NAVIGASI GESER --- */}
        <AnimatePresence>
          {selectedImg && (
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 backdrop-blur-sm" 
              onClick={() => setSelectedImg(null)}
            >
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }} 
                animate={{ scale: 1, opacity: 1 }} 
                exit={{ scale: 0.9, opacity: 0 }} 
                className="relative max-w-4xl w-full flex flex-col items-center" 
                onClick={(e) => e.stopPropagation()}
              >
                {/* Tombol Close */}
                <button onClick={() => setSelectedImg(null)} className="absolute -top-12 right-0 text-white hover:text-accent transition-colors p-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>

                {/* Navigasi Kiri (Desktop) */}
                <button 
                  onClick={handleModalPrev} 
                  className="absolute left-0 md:-left-20 top-1/2 -translate-y-1/2 bg-white/5 hover:bg-white/20 p-3 rounded-full text-white transition-all hidden md:block border border-white/10"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>

                {/* Gambar Utama */}
                <AnimatePresence mode="wait">
                  <motion.img 
                    key={selectedImg.src}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.3 }}
                    src={selectedImg.src} 
                    alt={selectedImg.alt} 
                    className="w-full h-auto max-h-[75vh] object-contain rounded-lg shadow-2xl mx-auto" 
                  />
                </AnimatePresence>

                <div className="text-white mt-6 text-center">
                    <p className="font-bold text-lg">{selectedImg.alt}</p>
                    <p className="text-white/40 text-xs mt-1">Gunakan keyboard panah atau klik samping untuk geser</p>
                </div>

                {/* Navigasi Kanan (Desktop) */}
                <button 
                  onClick={handleModalNext} 
                  className="absolute right-0 md:-right-20 top-1/2 -translate-y-1/2 bg-white/5 hover:bg-white/20 p-3 rounded-full text-white transition-all hidden md:block border border-white/10"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>

                {/* Navigasi Mobile */}
                <div className="flex gap-12 mt-6 md:hidden">
                    <button onClick={handleModalPrev} className="bg-white/10 p-4 rounded-full text-white active:bg-white/30">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <button onClick={handleModalNext} className="bg-white/10 p-4 rounded-full text-white active:bg-white/30">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </ThemeProvider>
  );
}